import eventEmitter, { EVENTS } from '../services/eventService';
import { idKey, toEpochMs } from './time';
import type { CachedComment, CachedHazard, CachedReaction, UserStore } from './store';

/**
 * The only writer of the hazards, comments, and reactions cache tables.
 * Server deletes remove rows. Local unsynced comments (`legacyPending`) are
 * kept across cache replacement.
 */

const CACHE_OMIT = new Set([
  'deleted',
  'deletedNum',
  'last_synced',
  'sync_error',
  'sync_attempts',
  'sync_dead',
  'tag_source',
  'migrated',
  'local_only',
]);

function serverFields(row: Record<string, unknown>): Record<string, unknown> {
  const copy: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!CACHE_OMIT.has(key)) {
      copy[key] = value;
    }
  }
  return copy;
}

function notify(): void {
  eventEmitter.emit(EVENTS.CACHE_CHANGED);
}

export async function applyServerRows(
  store: UserStore,
  rows: {
    hazards?: Array<Record<string, unknown>>;
    comments?: Array<Record<string, unknown>>;
    reactions?: Array<Record<string, unknown>>;
  }
): Promise<void> {
  await hydrateDroppedPhotoIds(store);
  await store.transaction('rw', [store.hazards, store.comments, store.reactions], async () => {
    for (const row of rows.hazards ?? []) {
      await upsertHazard(store, row);
    }
    for (const row of rows.comments ?? []) {
      await upsertComment(store, row, false);
    }
    for (const row of rows.reactions ?? []) {
      await upsertReaction(store, row);
    }
  });
  await persistDroppedPhotoIds(store);
  notify();
}

export interface RangeReplacePlan {
  upserts: Array<Record<string, unknown>>;
  removeIds?: string[];
  /** Cached rows that remain but fall outside the feed window. */
  demoteIds?: string[];
}

export async function replaceRange(store: UserStore, plan: RangeReplacePlan): Promise<void> {
  await hydrateDroppedPhotoIds(store);
  await store.transaction('rw', [store.hazards, store.comments, store.reactions], async () => {
    if (plan.removeIds && plan.removeIds.length > 0) {
      await removeHazardRows(store, plan.removeIds);
    }
    for (const id of plan.demoteIds ?? []) {
      await store.hazards.update(id, { inWindow: 0 });
    }
    for (const row of plan.upserts) {
      await upsertHazard(store, row);
    }
  });
  await persistDroppedPhotoIds(store);
  notify();
}

export async function removeHazards(store: UserStore, ids: string[]): Promise<void> {
  if (ids.length === 0) {
    return;
  }
  await store.transaction('rw', [store.hazards, store.comments, store.reactions], async () => {
    await removeHazardRows(store, ids);
  });
  notify();
}

const DROPPED_PHOTO_META_PREFIX = 'droppedPhotos:';

export function isSyncMetaKey(key: string): boolean {
  return (
    key === 'lastCommunitySync' ||
    key.startsWith('etag:') ||
    key.startsWith('comments_sync_') ||
    key.startsWith(DROPPED_PHOTO_META_PREFIX)
  );
}

/** Drop feed and comment cursors. The tenant id on the user store stays. */
export async function clearSyncMeta(store: UserStore): Promise<void> {
  const rows = await store.meta.toArray();
  const drop = rows.filter((row) => isSyncMetaKey(row.key)).map((row) => row.key);
  if (drop.length > 0) {
    await store.meta.bulkDelete(drop);
  }
  clearDroppedPhotoSession(store.name);
}

export async function clearCache(store: UserStore): Promise<void> {
  await store.transaction('rw', [store.hazards, store.comments, store.reactions, store.meta], async () => {
    await store.hazards.clear();
    await store.reactions.clear();
    const pending = await store.comments.filter((row) => row.legacyPending === 1).toArray();
    await store.comments.clear();
    if (pending.length > 0) {
      await store.comments.bulkPut(pending);
    }
    await clearSyncMeta(store);
  });
  notify();
}

export async function markViewed(
  store: UserStore,
  id: string,
  viewedAt: number = Date.now()
): Promise<void> {
  const updated = await store.hazards.update(id, { viewedAt });
  if (updated) {
    notify();
  }
}

/**
 * Photo ids removed by a newer read of the same hazard. A stale feed snapshot
 * still contains them until the list cache expires, and `utc_timestamp` does
 * not move when a photo is deleted, so the ids themselves are the order.
 * Session memory is checked synchronously; the meta row survives reload.
 */
const droppedPhotosByStore = new Map<string, Map<string, Set<number>>>();
const dirtyDroppedPhotos = new Map<string, Set<string>>();
const hydratedDroppedPhotos = new Set<string>();
const hydratingDroppedPhotos = new Map<string, Promise<void>>();

function droppedPhotoMetaKey(hazardId: string): string {
  return `${DROPPED_PHOTO_META_PREFIX}${hazardId}`;
}

function readPhotoId(item: unknown): number | null {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const photoId = (item as { photoId?: unknown }).photoId;
  if (typeof photoId !== 'number' || !Number.isFinite(photoId) || photoId <= 0) {
    return null;
  }
  return photoId;
}

function photoIdsOf(media: unknown): number[] {
  if (!Array.isArray(media)) {
    return [];
  }
  const ids: number[] = [];
  for (const item of media) {
    const photoId = readPhotoId(item);
    if (photoId != null && !ids.includes(photoId)) {
      ids.push(photoId);
    }
  }
  return ids;
}

/**
 * Photo ids removed from the gallery. A shorter `media[]` during video
 * processing is not a delete: the id is recorded only when `image_data`
 * shrank and the URL aligned with that id is gone.
 */
export function photoIdsDropped(
  previousMedia: unknown,
  incomingMedia: unknown,
  incomingImages?: unknown,
  previousImages?: unknown
): number[] {
  if (!Array.isArray(previousMedia) || !Array.isArray(incomingMedia)) {
    return [];
  }
  if (!Array.isArray(incomingImages) || !Array.isArray(previousImages)) {
    return [];
  }
  if (incomingImages.length >= previousImages.length) {
    return [];
  }
  const stillThere = new Set(
    incomingImages.filter((item): item is string => typeof item === 'string')
  );
  const nextIds = new Set(photoIdsOf(incomingMedia));
  const dropped: number[] = [];
  for (let index = 0; index < previousMedia.length; index += 1) {
    const id = readPhotoId(previousMedia[index]);
    if (id == null || nextIds.has(id) || dropped.includes(id)) {
      continue;
    }
    const url = previousImages[index];
    if (typeof url !== 'string' || stillThere.has(url)) {
      continue;
    }
    dropped.push(id);
  }
  return dropped;
}

function droppedPhotoIdsFor(storeName: string, hazardId: string): Set<number> {
  return droppedPhotosByStore.get(storeName)?.get(hazardId) ?? new Set<number>();
}

function rememberDroppedPhotoIds(storeName: string, hazardId: string, ids: number[], trackDirty = true): void {
  if (ids.length === 0) {
    return;
  }
  let byHazard = droppedPhotosByStore.get(storeName);
  if (!byHazard) {
    byHazard = new Map();
    droppedPhotosByStore.set(storeName, byHazard);
  }
  let known = byHazard.get(hazardId);
  if (!known) {
    known = new Set();
    byHazard.set(hazardId, known);
  }
  let added = false;
  for (const id of ids) {
    if (!known.has(id)) {
      known.add(id);
      added = true;
    }
  }
  if (!added || !trackDirty) {
    return;
  }
  let dirty = dirtyDroppedPhotos.get(storeName);
  if (!dirty) {
    dirty = new Set();
    dirtyDroppedPhotos.set(storeName, dirty);
  }
  dirty.add(hazardId);
}

function withoutDroppedPhotos(row: Record<string, unknown>, suppressed: Set<number>): Record<string, unknown> {
  if (!Array.isArray(row.media) || suppressed.size === 0) {
    return row;
  }
  const media = row.media;
  const dropAt = media.map((item) => {
    const photoId = readPhotoId(item);
    return photoId != null && suppressed.has(photoId);
  });
  if (!dropAt.some(Boolean)) {
    return row;
  }
  const next: Record<string, unknown> = {
    ...row,
    media: media.filter((_, index) => !dropAt[index]),
  };
  if (Array.isArray(row.image_data)) {
    next.image_data = row.image_data.filter((_, index) => index >= dropAt.length || !dropAt[index]);
  }
  if (typeof row.image_count === 'number' && Array.isArray(next.image_data)) {
    next.image_count = next.image_data.length;
  }
  return next;
}

export function clearDroppedPhotoSession(storeName: string): void {
  droppedPhotosByStore.delete(storeName);
  dirtyDroppedPhotos.delete(storeName);
  hydratedDroppedPhotos.delete(storeName);
  hydratingDroppedPhotos.delete(storeName);
}

export function hydrateDroppedPhotoIds(store: UserStore): Promise<void> {
  if (hydratedDroppedPhotos.has(store.name)) {
    return Promise.resolve();
  }
  const inflight = hydratingDroppedPhotos.get(store.name);
  if (inflight) {
    return inflight;
  }
  const pending = (async () => {
    const rows = await store.meta.toArray();
    for (const row of rows) {
      if (!row.key.startsWith(DROPPED_PHOTO_META_PREFIX)) {
        continue;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(row.value);
      } catch (error) {
        if (!(error instanceof SyntaxError)) {
          throw error;
        }
        continue;
      }
      if (!Array.isArray(parsed)) {
        continue;
      }
      const ids = parsed.filter((item): item is number => typeof item === 'number' && Number.isFinite(item) && item > 0);
      rememberDroppedPhotoIds(store.name, row.key.slice(DROPPED_PHOTO_META_PREFIX.length), ids, false);
    }
    hydratedDroppedPhotos.add(store.name);
  })();
  hydratingDroppedPhotos.set(store.name, pending);
  return pending.then(
    () => {
      hydratingDroppedPhotos.delete(store.name);
    },
    (error: unknown) => {
      hydratingDroppedPhotos.delete(store.name);
      throw error;
    }
  );
}

export async function persistDroppedPhotoIds(store: UserStore): Promise<void> {
  const bucket = dirtyDroppedPhotos.get(store.name);
  if (!bucket || bucket.size === 0) {
    return;
  }
  const batch = Array.from(bucket);
  bucket.clear();
  for (const hazardId of batch) {
    const ids = Array.from(droppedPhotoIdsFor(store.name, hazardId)).sort((a, b) => a - b);
    const key = droppedPhotoMetaKey(hazardId);
    try {
      if (ids.length === 0) {
        await store.meta.delete(key);
      } else {
        await store.meta.put({ key, value: JSON.stringify(ids) });
      }
    } catch (error) {
      bucket.add(hazardId);
      throw error;
    }
  }
}

/** Record ids dropped by a read that does not write Dexie (hazard_updated). Memory updates before the meta write. */
export function noteDroppedPhotoIds(store: UserStore, hazardId: string, ids: number[]): void {
  rememberDroppedPhotoIds(store.name, hazardId, ids);
  void persistDroppedPhotoIds(store).catch((error: unknown) => {
    const bucket = dirtyDroppedPhotos.get(store.name) ?? new Set<string>();
    bucket.add(hazardId);
    dirtyDroppedPhotos.set(store.name, bucket);
    return error;
  });
}

async function upsertHazard(store: UserStore, row: Record<string, unknown>): Promise<void> {
  const id = typeof row.id === 'string' ? row.id : '';
  if (!id) {
    return;
  }
  if (row.deleted === true) {
    await removeHazardRows(store, [id]);
    return;
  }
  const sortTs = toEpochMs(row.utc_timestamp);
  if (sortTs == null) {
    return;
  }
  const existing = await store.hazards.get(id);
  const dropped = photoIdsDropped(existing?.media, row.media, row.image_data, existing?.image_data);
  if (dropped.length > 0) {
    rememberDroppedPhotoIds(store.name, id, dropped);
  }
  const suppressed = droppedPhotoIdsFor(store.name, id);
  const source = suppressed.size > 0 ? withoutDroppedPhotos(row, suppressed) : row;
  const cached: CachedHazard = {
    ...serverFields(source),
    id,
    idKey: idKey(id),
    sortTs,
  };
  if (existing?.viewedAt != null) {
    cached.viewedAt = existing.viewedAt;
  }
  if (row.inWindow === 0 || row.inWindow === 1) {
    cached.inWindow = row.inWindow;
  } else if (existing?.inWindow === 0 || existing?.inWindow === 1) {
    cached.inWindow = existing.inWindow;
  }
  await store.hazards.put(cached);
}

async function upsertComment(
  store: UserStore,
  row: Record<string, unknown>,
  legacyPending: boolean
): Promise<void> {
  const id = typeof row.id === 'string' ? row.id : '';
  const incidentId = typeof row.incident_id === 'string' ? row.incident_id : '';
  if (!id || !incidentId) {
    return;
  }
  if (row.deleted === true) {
    const existing = await store.comments.get(id);
    if (existing?.legacyPending === 1) {
      return;
    }
    await store.comments.delete(id);
    return;
  }
  const sortTs = toEpochMs(row.created_at) ?? 0;
  const cached: CachedComment = {
    ...serverFields(row),
    id,
    incident_id: incidentId,
    sortTs,
  };
  if (legacyPending) {
    cached.legacyPending = 1;
  }
  await store.comments.put(cached);
}

/** Display write. Keeps local_only, last_synced, and deleted, which the server mirror omits. */
export async function writeDisplayComment(store: UserStore, row: Record<string, unknown>): Promise<void> {
  const id = typeof row.id === 'string' ? row.id : '';
  const incidentId = typeof row.incident_id === 'string' ? row.incident_id : '';
  if (!id || !incidentId) {
    return;
  }
  const existing = await store.comments.get(id);
  const cached: CachedComment = {
    ...row,
    id,
    incident_id: incidentId,
    sortTs: toEpochMs(row.created_at) ?? existing?.sortTs ?? 0,
  };
  if (existing?.legacyPending === 1 || row.legacyPending === 1) {
    cached.legacyPending = 1;
  }
  await store.comments.put(cached);
  notify();
}

export async function writeDisplayReaction(store: UserStore, row: Record<string, unknown>): Promise<void> {
  const id = typeof row.id === 'string' ? row.id : '';
  const contentId = typeof row.content_id === 'string' ? row.content_id : '';
  if (!id || !contentId) {
    return;
  }
  const cached: CachedReaction = {
    ...row,
    id,
    content_id: contentId,
  };
  await store.reactions.put(cached);
  notify();
}

async function upsertReaction(store: UserStore, row: Record<string, unknown>): Promise<void> {
  const id = typeof row.id === 'string' ? row.id : '';
  const contentId = typeof row.content_id === 'string' ? row.content_id : '';
  if (!id || !contentId) {
    return;
  }
  if (row.deleted === true) {
    await store.reactions.delete(id);
    return;
  }
  const cached: CachedReaction = {
    ...serverFields(row),
    id,
    content_id: contentId,
  };
  await store.reactions.put(cached);
}

async function removeHazardRows(store: UserStore, ids: string[]): Promise<void> {
  for (const id of ids) {
    await store.hazards.delete(id);
    const comments = await store.comments.where('incident_id').equals(id).toArray();
    const drop = comments.filter((row) => row.legacyPending !== 1).map((row) => row.id);
    if (drop.length > 0) {
      await store.comments.bulkDelete(drop);
    }
    await store.reactions.where('content_id').equals(id).delete();
  }
}

export async function putLegacyPendingComment(
  store: UserStore,
  row: Record<string, unknown>
): Promise<void> {
  await upsertComment(store, row, true);
}
